import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Badge, InputGroup } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { getVehicles } from '../services/vehicleService';
import { getBrands, getModelsByBrand } from '../services/vehicleDataService';
import { DEFAULT_FILTER_STATE, FILTER_PARAMS } from '../constants/filterConstants';
import { parseUrlParamsToFilters, filtersToUrlParams, getCleanFilters } from '../utils/filterUtils';
import api from '../services/api';
import * as XLSX from 'xlsx';


/**
 * ERP Odaklı Stok Listesi Sayfası
 * Fotoğraf sistemi kaldırıldı, stok kontrolü odaklı
 */
const VehiclesPage = () => {
    const location = useLocation();

    // Durum değişkenleri
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtre seçenekleri
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);

    // ERP Odaklı Filtreler
    const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    const [quickSearch, setQuickSearch] = useState(''); // Plaka/Motor/Şase arama

    // Sıralama
    const [sortOption, setSortOption] = useState('newest');

    // ERP Sıralama seçenekleri
    const sortOptions = [
        { value: 'newest', label: 'En Yeni Eklenler' },
        { value: 'oldest', label: 'En Eski Stok' },
        { value: 'price_asc', label: 'Alış Fiyatı (Düşük-Yüksek)' },
        { value: 'price_desc', label: 'Alış Fiyatı (Yüksek-Düşük)' },
        { value: 'year_desc', label: 'Model Yılı (Yeni-Eski)' },
        { value: 'mileage_asc', label: 'Kilometre (Az-Çok)' }
    ];

    // Araçları yükle
    const fetchVehicles = async () => {
        const currentFilters = filters; // ← State'ten al
        try {
            setLoading(true);
            const validFilters = getCleanFilters(currentFilters);

            // Hızlı arama ekleme
            if (quickSearch.trim()) {
                validFilters.search = quickSearch.trim();
            }

            if (sortOption) {
                validFilters.sort = sortOption;
            }

            // Pagination parametreleri ekle
            validFilters.page = pagination.page;
            validFilters.limit = pagination.limit;

            console.log("ERP Stok filtreler:", validFilters);

            const result = await getVehicles(validFilters);

            if (result.success) {
                setVehicles(result.vehicles);
                // Backend'den pagination bilgilerini al
                setPagination(prev => ({
                    ...prev,
                    total: result.total,
                    pages: result.pages,
                    currentPage: result.currentPage
                }));
            } else {
                setError('Stok verileri yüklenirken hata oluştu');
            }
        } catch (error) {
            setError('Bağlantı hatası');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ ROBUST & SUSTAINABLE: Pagination Loop ile Excel Export
    const handleExportExcel = async () => {
        // Tarih kontrolü
        const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const dateTo = filters.dateTo ? new Date(filters.dateTo) : null;

        if (!dateFrom || !dateTo) {
            alert('Tarih aralığı seçin!');
            return;
        }

        try {
            setLoading(true);

            // ✅ TÜM araçları çek - PAGINATION LOOP
            console.log('📦 Tüm araçlar yükleniyor (sayfa sayfa)...');

            let allVehicles = [];
            let currentPage = 1;
            let totalPages = 1;

            // İlk sayfa ile başla
            do {
                console.log(`📄 Sayfa ${currentPage}/${totalPages} yükleniyor...`);

                const result = await getVehicles({
                    dateFrom: filters.dateFrom,
                    dateTo: filters.dateTo,
                    limit: 50,  // Batch size
                    page: currentPage
                });

                if (!result.success) {
                    throw new Error('Araçlar yüklenirken hata oluştu');
                }

                // Araçları ekle
                allVehicles = [...allVehicles, ...result.vehicles];

                // Total pages güncelle
                totalPages = result.pages || 1;

                console.log(`   ✅ ${result.vehicles.length} araç eklendi (Toplam: ${allVehicles.length})`);

                currentPage++;

            } while (currentPage <= totalPages);

            console.log(`✅ TOPLAM ${allVehicles.length} araç yüklendi`);

            if (allVehicles.length === 0) {
                alert('Seçili tarih aralığında araç bulunamadı!');
                setLoading(false);
                return;
            }

            // Ay adları
            const ayAdlari = [
                'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
            ];

            // Tarih filtreleme
            const alinanAraclar = allVehicles.filter(vehicle => {
                if (!vehicle.purchaseDate) return false;
                const purchaseDate = new Date(vehicle.purchaseDate);
                return purchaseDate >= dateFrom && purchaseDate <= dateTo;
            });

            const satilanAraclar = allVehicles.filter(vehicle => {
                if (vehicle.status !== 'sold' || !vehicle.saleDate) return false;
                const saleDate = new Date(vehicle.saleDate);
                return saleDate >= dateFrom && saleDate <= dateTo;
            });

            console.log(`📊 Alınan: ${alinanAraclar.length}, Satılan: ${satilanAraclar.length}`);

            if (alinanAraclar.length === 0 && satilanAraclar.length === 0) {
                alert('Seçili tarih aralığında alış/satış bulunamadı!');
                setLoading(false);
                return;
            }

            const maxRows = Math.max(alinanAraclar.length, satilanAraclar.length);

            // Headers
            const headers = [
                'Alınan Araç No', 'Alınan Plaka', 'Alınan Marka - Model', 'Alış Tarihi',
                'Aldığımızın Adı', 'Aldığımızın ID (TCKN/VKN)', 'Alış Fatura No',
                '',
                'Satılan Araç No', 'Satılan Plaka', 'Satılan Marka - Model', 'Satış Tarihi', 'Satış Türü'
            ];

            // Satırlar
            const rows = [];
            for (let i = 0; i < maxRows; i++) {
                const alinanArac = alinanAraclar[i];
                const satilanArac = satilanAraclar[i];

                let alinanSiraNo = '', alinanPlaka = '', alinanMarka = '', alisTarihi = '', aliciAdi = '', aliciId = '';
                if (alinanArac) {
                    alinanSiraNo = i + 1;
                    alinanPlaka = alinanArac.plateNumber || '';
                    alinanMarka = `${alinanArac.brand || ''} - ${alinanArac.model || ''}`.trim();
                    alisTarihi = new Date(alinanArac.purchaseDate).toLocaleDateString('tr-TR');
                    aliciAdi = alinanArac.purchasedFrom?.name || '';
                    aliciId = alinanArac.purchasedFrom?.idNumber || '';
                }

                let satilanSiraNo = '', satilanPlaka = '', satilanMarka = '', satisTarihi = '', satisTuru = '';
                if (satilanArac) {
                    satilanSiraNo = i + 1;
                    satilanPlaka = satilanArac.plateNumber || '';
                    satilanMarka = `${satilanArac.brand || ''} - ${satilanArac.model || ''}`.trim();
                    satisTarihi = new Date(satilanArac.saleDate).toLocaleDateString('tr-TR');
                    satisTuru = satilanArac.hasContract ? 'Senetli' : 'Direk';
                }
                rows.push([
                    alinanSiraNo, alinanPlaka, alinanMarka, alisTarihi, aliciAdi, aliciId, '',
                    '',
                    satilanSiraNo, satilanPlaka, satilanMarka, satisTarihi, satisTuru
                ]);
            }

            // Excel oluştur
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

            ws['!cols'] = [
                { width: 12 }, { width: 15 }, { width: 25 }, { width: 12 },
                { width: 30 }, { width: 18 }, { width: 15 }, { width: 3 },
                { width: 12 }, { width: 15 }, { width: 25 }, { width: 12 }, { width: 15 }
            ];

            // Senetli satışları renklendirme
            for (let i = 0; i < rows.length; i++) {
                const rowIndex = i + 2;
                const satisTuru = rows[i][12];

                if (satisTuru === 'Senetli') {
                    const cellAddress = `M${rowIndex}`;
                    if (ws[cellAddress]) {
                        ws[cellAddress].s = {
                            font: { bold: false, sz: 11 },
                            fill: { fgColor: { rgb: "FFFFE6E6" } },
                            alignment: { horizontal: "center" }
                        };
                    }
                }
            }

            // Header styling
            const alisCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1'];
            alisCells.forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = {
                        font: { bold: true, sz: 11 },
                        fill: { fgColor: { rgb: "FFFFFF00" } },
                        alignment: { horizontal: "center" }
                    };
                }
            });

            const satisCells = ['I1', 'J1', 'K1', 'L1', 'M1'];
            satisCells.forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = {
                        font: { bold: true, sz: 11 },
                        fill: { fgColor: { rgb: "FF00FF00" } },
                        alignment: { horizontal: "center" }
                    };
                }
            });

            if (ws['H1']) {
                ws['H1'].s = {
                    font: { bold: true, sz: 11 },
                    alignment: { horizontal: "center" }
                };
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Muhasebe Araç Listesi');

            // Dosya adı
            const ay = ayAdlari[dateFrom.getMonth()];
            const yil = dateFrom.getFullYear();
            const dosyaAdi = `${ay}_${yil}_Muhasebe_Alis_Satis_Raporu.xlsx`;

            XLSX.writeFile(wb, dosyaAdi);

            // ✅ İstatistikleri hesapla
            const senetliCount = rows.filter(row => row[12] === 'Senetli').length;
            const direkCount = rows.filter(row => row[12] === 'Direk').length;

            console.log(`📊 Excel exported: ${alinanAraclar.length} alınan, ${satilanAraclar.length} satılan`);
            console.log(`   💳 Senetli: ${senetliCount}, 💵 Direk: ${direkCount}`);
            console.log(`📁 Dosya: ${dosyaAdi}`);

            alert(`✅ Excel oluşturuldu!\n\nToplam: ${allVehicles.length} araç\nAlınan: ${alinanAraclar.length}\nSatılan: ${satilanAraclar.length}\n\n💳 Senetli: ${senetliCount}\n💵 Direk: ${direkCount}`);



        } catch (error) {
            console.error('❌ Excel export hatası:', error);
            alert('Hata: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // İlk yüklemede dropdown'ları yükle
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                setLoadingDropdowns(true);
                const brandsResponse = await getBrands();
                if (brandsResponse.success) {
                    setBrands(brandsResponse.data);
                }
            } catch (error) {
                console.error('Filtre seçenekleri yükleme hatası:', error);
            } finally {
                setLoadingDropdowns(false);
            }
        };

        loadFilterOptions();
    }, []); // ← Sadece ilk yüklemede

    // Filtre veya sayfa değişince araçları yükle
    useEffect(() => {
        fetchVehicles();
    }, [filters, pagination.page]);

    // Marka değiştiğinde modelleri yükle
    useEffect(() => {
        const loadModels = async () => {
            if (!filters.brand) {
                setModels([]);
                return;
            }

            try {
                const response = await getModelsByBrand(filters.brand);
                if (response.success) {
                    setModels(response.data);
                }
            } catch (error) {
                console.error('Model yükleme hatası:', error);
                setModels([]);
            }
        };

        loadModels();
    }, [filters.brand]);

    // Filtre değişiklikleri
    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        if (name === 'brand') {
            setFilters(prev => ({
                ...prev,
                model: '',
                bodyType: '',
                [name]: value
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Filtre değiştiğinde sayfa 1'e dön
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Hızlı arama
    const handleQuickSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchVehicles(filters);
    };

    // Filtre gönderme
    const handleSearch = (e) => {
        e.preventDefault();

        const updatedFilters = { ...filters };
        if (sortOption) {
            updatedFilters.sort = sortOption;
        }

        const queryParams = filtersToUrlParams(updatedFilters).toString();
        window.history.replaceState(null, '', `${location.pathname}?${queryParams}`);

        fetchVehicles(updatedFilters);
    };

    // Sıralama değişimi
    const handleSortChange = (e) => {
        const newSort = e.target.value;
        setSortOption(newSort);

        // ❌ YANLIŞ - Orijinal array'i mutate ediyor:
        // let sortedVehicles = [...vehicles];

        // ✅ DOĞRU - Yeni array oluştur:
        let sortedVehicles = [...vehicles];  // Spread ile kopya oluştur

        switch (newSort) {
            case 'price_asc':
                sortedVehicles.sort((a, b) => (a.purchasePrice || 0) - (b.purchasePrice || 0));
                break;
            case 'price_desc':
                sortedVehicles.sort((a, b) => (b.purchasePrice || 0) - (a.purchasePrice || 0));
                break;
            case 'year_desc':
                sortedVehicles.sort((a, b) => (b.year || 0) - (a.year || 0));
                break;
            case 'mileage_asc':
                sortedVehicles.sort((a, b) => (a.mileage || 0) - (b.mileage || 0));
                break;
            case 'newest':
                sortedVehicles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
            case 'oldest':
                sortedVehicles.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                break;
            default:
                break;
        }
        setVehicles(sortedVehicles);
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            page: newPage
        }));
    };

    const handlePreviousPage = () => {
        if (pagination.page > 1) {
            handlePageChange(pagination.page - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination.page < pagination.pages) {
            handlePageChange(pagination.page + 1);
        }
    };

    // ✅ UPDATED: Düzeltilmiş Status Badge'i
    const getStatusBadge = (status) => {
        const variants = {
            'active': 'success',
            'reserved': 'warning',
            'sold': 'danger',
            'archived': 'secondary'
        };

        const labels = {
            'active': 'Stokta',
            'reserved': 'Rezerve',
            'sold': 'Satıldı',
            'archived': 'Arşiv'
        };

        return (
            <Badge
                bg={variants[status] || 'secondary'}
                className="fs-6"
            >
                {labels[status] || status}
            </Badge>
        );
    };

    // ✅ NEW: Kaynak Badge'i (Galeri/Dışarıdan)
    const getSourceBadge = (isExternal) => {
        if (isExternal) {
            return <Badge bg="warning" title="Dışarıdan alınan araç">📤 Dışarıdan</Badge>;
        } else {
            return <Badge bg="primary" title="Galeri stokundaki araç">🏢 Galeri</Badge>;
        }
    };

    const getInsuranceStatusBadge = (vehicle) => {
        console.log("🚗 Vehicle Data:", vehicle.plateNumber);
        console.log("🔍 Vehicle Status:", vehicle.status);
        console.log("📄 DocumentSummary:", vehicle.documentSummary);
        console.log("🔍 hasSigortaIptalPolicesi:", vehicle.documentSummary?.hasSigortaIptalPolicesi);

        const insurance = vehicle.trafficInsurance;
        const docSummary = vehicle.documentSummary;

        // ✅ ÖNCELİK: Satılan araçlar için iptal poliçesi kontrolü
        if (vehicle.status === 'sold') {
            console.log("🔍 Status SOLD kontrolü geçti");
            if (docSummary?.hasSigortaIptalPolicesi) {
                console.log("✅ İptal poliçesi bulundu!");
                return <Badge bg="success" className="fs-6" title="Sigorta iptal poliçesi yüklendi">✅ İptal OK</Badge>;
            }
            console.log("❌ Satıldı ama iptal poliçesi yok");
            return <Badge bg="warning" className="fs-6" title="UYARI: Satış tamam ama sigorta iptal belgesi eksik">⚠️ Belgeler Eksik</Badge>;
        }

        // Arşivlenen araçlar için
        if (vehicle.status === 'archived') {
            if (docSummary?.hasSigortaIptalPolicesi) {
                return <Badge bg="success" className="fs-6" title="Sigorta iptal edildi">✅ Tamamlandı</Badge>;
            }
            return <Badge bg="warning" className="fs-6" title="Arşiv bilgileri eksik">⚠️ Eksik</Badge>;
        }

        // Aktif araçlar için hasInsurance kontrolü
        if (!insurance?.hasInsurance) {
            return <Badge bg="danger" className="fs-6" title="Sigorta yok!">❌ Yok</Badge>;
        }

        // Aktif araçlar için
        if (vehicle.status === 'active' || vehicle.status === 'reserved') {
            if (insurance.endDate && new Date(insurance.endDate) < new Date()) {
                return <Badge bg="danger" className="fs-6" title="Sigorta süresi dolmuş!">⏰ Süresi Doldu</Badge>;
            }
            return <Badge bg="success" className="fs-6" title="Sigorta aktif">✅ Aktif</Badge>;
        }

        return <Badge bg="secondary" className="fs-6" title="Belirsiz durum">❓ Belirsiz</Badge>;
    };

    // Number formatting
    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString('tr-TR');
    };

    return (
        <Container className="py-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>📦 ERP Stok Yönetimi</h1>
                <Button as={Link} to="/vehicles/add" variant="primary">
                    <FaPlus className="me-2" />
                    Yeni Araç Ekle
                </Button>
            </div>

            {/* Stok Listesi */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">📊 Stok Listesi ({vehicles.length} araç)</h5>
                </Card.Header>

                {/* ✅ YENİ FİLTRE BÖLÜMİ */}
                <Card.Body className="border-bottom">
                    <Form onSubmit={handleSearch}>
                        {/* İlk Satır - Ana Filtreler */}
                        <Row className="g-3">
                            {/* Marka */}
                            <Col md={2}>
                                <Form.Select
                                    name="brand"
                                    value={filters.brand}
                                    onChange={handleFilterChange}
                                    disabled={loadingDropdowns}
                                >
                                    <option value="">Tüm Markalar</option>
                                    {brands.map(brand => (
                                        <option
                                            key={brand._id || brand.name || brand}
                                            value={brand.name || brand}
                                        >
                                            {brand.name || brand}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* Model */}
                            <Col md={2}>
                                <Form.Select
                                    name="model"
                                    value={filters.model}
                                    onChange={handleFilterChange}
                                    disabled={!filters.brand || models.length === 0}
                                >
                                    <option value="">Tüm Modeller</option>
                                    {models.map(model => (
                                        <option
                                            key={model._id || model.name || model}
                                            value={model.name || model}
                                        >
                                            {model.name || model}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* Durum */}
                            <Col md={2}>
                                <Form.Select
                                    name="status"
                                    value={filters.status || ''}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Tüm Durumlar</option>
                                    <option value="active">Stokta</option>
                                    <option value="reserved">Rezerve</option>
                                    <option value="sold">Satıldı</option>
                                    <option value="archived">Arşiv</option>
                                </Form.Select>
                            </Col>

                            {/* Kaynak */}
                            <Col md={2}>
                                <Form.Select
                                    name="isExternal"
                                    value={filters.isExternal || ''}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">Tüm Kaynaklar</option>
                                    <option value="false">🏢 Galeri</option>
                                    <option value="true">📤 Dışarıdan</option>
                                </Form.Select>
                            </Col>

                            {/* Yıl */}
                            <Col md={2}>
                                <Form.Control
                                    type="number"
                                    name="minYear"
                                    placeholder="Min Yıl"
                                    value={filters.minYear || ''}
                                    onChange={handleFilterChange}
                                    min="1990"
                                    max="2025"
                                />
                            </Col>

                            {/* Sıralama */}
                            <Col md={2}>
                                <Form.Select value={sortOption} onChange={handleSortChange}>
                                    {sortOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>

                        {/* İkinci Satır - Tarih ve Butonlar */}
                        <Row className="g-3 mt-2">
                            {/* Tarih Başlangıç */}
                            <Col md={2}>
                                <Form.Control
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom || ''}
                                    onChange={handleFilterChange}
                                    title="Başlangıç Tarihi"
                                />
                                <small className="text-muted">Başlangıç</small>
                            </Col>

                            {/* Tarih Bitiş */}
                            <Col md={2}>
                                <Form.Control
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo || ''}
                                    onChange={handleFilterChange}
                                    title="Bitiş Tarihi"
                                />
                                <small className="text-muted">Bitiş</small>
                            </Col>

                            {/* Filtrele Butonu */}
                            <Col md={2}>
                                <Button type="submit" variant="primary" className="w-100">
                                    <FaSearch className="me-1" />
                                    Filtrele
                                </Button>
                            </Col>

                            {/* Filtreleri Temizle */}
                            <Col md={3}>
                                <Button
                                    variant="danger"
                                    className="w-100"
                                    onClick={() => {
                                        setFilters(DEFAULT_FILTER_STATE);
                                        setQuickSearch('');
                                        setSortOption('newest');
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                        fetchVehicles(DEFAULT_FILTER_STATE);
                                    }}
                                >
                                    🗑️ Filtreleri Temizle
                                </Button>
                            </Col>

                            {/* Excel İndir */}
                            <Col md={3}>
                                <Button
                                    variant="success"
                                    className="w-100"
                                    onClick={handleExportExcel}
                                    disabled={vehicles.length === 0}
                                >
                                    📊 Excel İndir
                                </Button>
                            </Col>
                        </Row>

                        {/* Üçüncü satır - Sadece Hızlı arama */}
                        <Row className="g-3 mt-2">
                            <Col md={8}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Plaka, Şase No ile hızlı arama..."
                                        value={quickSearch}
                                        onChange={(e) => setQuickSearch(e.target.value)}
                                    />
                                    <Button variant="outline-secondary" onClick={handleQuickSearch}>
                                        <FaSearch />
                                    </Button>
                                </InputGroup>
                            </Col>
                            <Col md={4}>
                                <Badge bg="info" className="align-self-center h-100 d-flex align-items-center justify-content-center">
                                    {vehicles.length} araç bulundu
                                </Badge>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>

                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger m-3">{error}</div>
                    ) : vehicles.length === 0 ? (
                        <div className="alert alert-info m-3">Stokta araç bulunamadı</div>
                    ) : (
                        <Table striped hover responsive className="mb-0">
                            <thead className="table-dark">
                                <tr>
                                    <th>Plaka</th>
                                    <th>Araç</th>
                                    <th>Yıl</th>
                             
                                    <th>Alış Fiyatı</th>
                                    <th>Satış Fiyatı</th>
                                    <th>Durum</th>
                             
                                    <th>Sigorta</th>
                               
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map((vehicle) => (
                                    <tr key={vehicle._id}>
                                        <td>
                                            <span className="fw-bold">{vehicle.plateNumber}</span>

                                        </td>
                                        <td>
                                            {vehicle.brand} {vehicle.model}
                                            <br />
                                            <small className="text-muted">
                                                {vehicle.fuelType} • {vehicle.transmission}
                                            </small>
                                        </td>
                                        <td>{vehicle.year}</td>
                                        
                                        <td className="text-success">
                                           
                                                {vehicle.purchasePrice ?
                                                    `${formatNumber(vehicle.purchasePrice)} ₺` :
                                                    <span className="text-muted">-</span>
                                                }
                                          
                                        </td>
                                        <td>
                                            {vehicle.salePrice ?
                                                <span className="text-primary">{formatNumber(vehicle.salePrice)} ₺</span> :
                                                <span className="text-muted">-</span>
                                            }
                                        </td>
                                        <td>{getStatusBadge(vehicle.status)}</td>
                                       
                                        <td>{getInsuranceStatusBadge(vehicle)}</td>

                                        
                                        <td>
                                            <div className="d-flex gap-1">
                                                <Button
                                                    as={Link}
                                                    to={`/vehicles/${vehicle._id}`}
                                                    size="sm"
                                                    variant="outline-primary"
                                                >
                                                    📄 Detay
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() => {
                                                        // api.js'den baseURL'yi al
                                                        const baseURL = api.defaults.baseURL;

                                                        // Eğer relative path ise (/api) tam URL'ye çevir
                                                        const fullBaseURL = baseURL.startsWith('/')
                                                            ? `${window.location.origin}${baseURL}`
                                                            : baseURL;

                                                        // '/api' kısmını '/api/label' ile değiştir
                                                        const labelUrl = fullBaseURL.replace('/api', '/api/label') + `/${vehicle._id}`;

                                                        console.log('Etiket URL:', labelUrl);
                                                        window.open(labelUrl, '_blank');
                                                    }}
                                                >
                                                    🏷️ Etiket
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-warning"
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            const response = await api.post(`/vehicles/${vehicle._id}/generate-ad-image`);

                                                            if (response.data.success) {
                                                                const imageUrl = response.data.imageUrl;

                                                                // ✅ HTML formatında kopyala (Sahibinden editörü için)
                                                                const htmlCode = `<p><br></p>
<p><br></p>
<div style="color: rgb(41, 41, 41);font-size: 14px;text-align: center;background-color: rgb(255, 255, 255);">
<img src="${imageUrl}" width="100%" height="auto" style="vertical-align: middle;">
</div>
<p><br></p>
<p><br></p>`;

                                                                // HTML kodunu clipboard'a kopyala
                                                                navigator.clipboard.writeText(htmlCode);

                                                                // Kullanıcıya bildir
                                                                alert('✅ Reklam görseli oluşturuldu!\n\nHTML kodu kopyalandı.\nSahibinden editörüne yapıştırabilirsiniz.');

                                                                // Görseli yeni sekmede aç (görsel kontrolü için)
                                                                window.open(imageUrl, '_blank');
                                                            }
                                                        } catch (error) {
                                                            console.error('Görsel oluşturma hatası:', error);
                                                            alert('❌ Hata: ' + error.message);
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                >
                                                    📱 Sahibinden
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {/* Pagination Controls */}
                    {vehicles.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                            <small className="text-muted">
                                Gösterilen: {vehicles.length} / Toplam: {pagination.total} araç
                            </small>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="me-2"
                                    onClick={handlePreviousPage}
                                    disabled={pagination.page <= 1}
                                >
                                    « Önceki
                                </Button>
                                <span className="mx-2">
                                    Sayfa {pagination.page} / {pagination.pages}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleNextPage}
                                    disabled={pagination.page >= pagination.pages}
                                >
                                    Sonraki »
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default VehiclesPage;